export const STAGE_COLOR_PALETTE = ['#F2556A', '#3B82F6', '#E8B339', '#2F9E5F'];

export const STAGE_COLORS = {
  open: '#F2556A',
  in_progress: '#3B82F6',
  waiting: '#E8B339',
  done: '#2F9E5F',
};

const STAGE_HEX = /^#[0-9A-F]{6}$/;

export function stageColor(column, index = 0) {
  const raw = String(column?.color || '').trim().toUpperCase();
  if (STAGE_HEX.test(raw)) return raw;
  const key = column?.key || column?.id;
  if (STAGE_COLORS[key]) return STAGE_COLORS[key];
  const safe = Number.isFinite(Number(index)) ? Math.abs(Number(index)) : 0;
  return STAGE_COLOR_PALETTE[safe % STAGE_COLOR_PALETTE.length];
}

export function nextStageColor(used = []) {
  const taken = new Set(used.map((color) => String(color || '').toUpperCase()));
  return STAGE_COLOR_PALETTE.find((color) => !taken.has(color))
    || STAGE_COLOR_PALETTE[used.length % STAGE_COLOR_PALETTE.length];
}

export function stageWash(hex, mix = 0.84) {
  const raw = String(hex || '').replace('#', '');
  if (!/^[0-9a-fA-F]{6}$/.test(raw)) return '#f1f2f4';
  const channels = [0, 2, 4].map((start) => parseInt(raw.slice(start, start + 2), 16));
  const mixed = channels.map((channel) => Math.round(channel + (255 - channel) * mix));
  return `rgb(${mixed[0]} ${mixed[1]} ${mixed[2]})`;
}

export function stageInk(hex) {
  const color = stageColor({ color: hex });
  const raw = color.slice(1);
  const r = parseInt(raw.slice(0, 2), 16);
  const g = parseInt(raw.slice(2, 4), 16);
  const b = parseInt(raw.slice(4, 6), 16);
  const luma = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  if (luma <= 0.82) return color;
  const darken = (channel) => Math.round(channel * 0.45);
  return `rgb(${darken(r)} ${darken(g)} ${darken(b)})`;
}

export const DEFAULT_TASK_COLUMNS = [
  { key: 'open', name: 'To Do', color: '#F2556A', sort_order: 0, progress_percent: 0, counts_toward_progress: true, marks_complete: false, visible_to_client: true },
  { key: 'in_progress', name: 'In Progress', color: '#3B82F6', sort_order: 1, progress_percent: 50, counts_toward_progress: true, marks_complete: false, visible_to_client: true },
  { key: 'waiting', name: 'Waiting', color: '#E8B339', sort_order: 2, progress_percent: 50, counts_toward_progress: true, marks_complete: false, visible_to_client: true },
  { key: 'done', name: 'Complete', color: '#2F9E5F', sort_order: 3, progress_percent: 100, counts_toward_progress: true, marks_complete: true, visible_to_client: true },
];

export function boardColumns(columns, { client = false } = {}) {
  const source = columns?.length ? columns : DEFAULT_TASK_COLUMNS;
  return [...source]
    .map((column) => {
      const key = column.key || column.id;
      const known = DEFAULT_TASK_COLUMNS.find((item) => item.key === key);
      return {
        id: key,
        columnId: column.columnId ?? (typeof column.id === 'number' ? column.id : null),
        key,
        name: column.name || column.label || known?.name || key,
        label: column.name || column.label || known?.name || key,
        sort_order: column.sort_order ?? known?.sort_order ?? 0,
        color: stageColor(column, column.sort_order ?? known?.sort_order ?? 0),
        progress_percent: Number(column.progress_percent ?? known?.progress_percent ?? 0),
        counts_toward_progress: column.counts_toward_progress ?? known?.counts_toward_progress ?? true,
        marks_complete: column.marks_complete ?? known?.marks_complete ?? false,
        visible_to_client: column.visible_to_client ?? known?.visible_to_client ?? true,
      };
    })
    .filter((column) => !client || column.visible_to_client)
    .sort((a, b) => a.sort_order - b.sort_order || (Number(a.columnId) || 0) - (Number(b.columnId) || 0));
}

export function isActiveTask(task, columns) {
  const column = boardColumns(columns).find((item) => item.key === task?.status);
  if (!column) {
    return task?.status === 'open' || task?.status === 'in_progress' || task?.status === 'waiting';
  }
  return column.counts_toward_progress && !column.marks_complete;
}

export function stageCaption(stage) {
  if (stage?.counts_toward_progress === false) {
    return 'Not counted';
  }
  if (stage?.task_count != null) {
    const count = Number(stage.task_count) || 0;
    return `${count} task${count === 1 ? '' : 's'} · ${stage.progress_percent ?? 0}%`;
  }
  if (stage?.state === 'done') return 'Completed';
  if (stage?.state === 'current') return 'In progress';
  return 'Upcoming';
}

/**
 * Progress is the weighted average of task weights times the stage percent.
 * Weight 0 and stages marked "not counted" are ignored.
 */
export function summarizeTaskProgress(tasks = [], columns = [], { hideInternal = false } = {}) {
  const cols = boardColumns(columns);
  const stats = Object.fromEntries(cols.map((column) => [column.key, { tasks: 0, remaining: 0 }]));
  let weight = 0;
  let earned = 0;

  for (const task of tasks) {
    const column = cols.find((item) => item.key === task.status);
    if (!column || !stats[column.key]) continue;
    stats[column.key].tasks += 1;
    const taskWeight = Number(task.progress_weight ?? 1);
    if (!column.counts_toward_progress || taskWeight <= 0) continue;
    const stagePercent = Math.max(0, Math.min(100, Number(column.progress_percent) || 0));
    weight += taskWeight;
    earned += taskWeight * stagePercent;
    stats[column.key].remaining += taskWeight * (100 - stagePercent);
  }

  const percent = weight > 0 ? Math.round(earned / weight) : 0;
  let focusKey = null;
  let bestRemaining = 0;
  for (const column of cols) {
    if (!column.counts_toward_progress || column.marks_complete) continue;
    const remaining = stats[column.key]?.remaining || 0;
    if (remaining > bestRemaining) {
      bestRemaining = remaining;
      focusKey = column.key;
    }
  }
  if (!focusKey && percent < 100) {
    focusKey = cols.find((column) => column.counts_toward_progress && !column.marks_complete)?.key || null;
  }

  const stages = cols
    .filter((column) => !hideInternal || column.visible_to_client || column.counts_toward_progress)
    .map((column) => {
      const info = stats[column.key] || { tasks: 0 };
      let state = 'upcoming';
      if (column.key === focusKey) state = 'current';
      else if (column.marks_complete && info.tasks > 0) state = 'done';
      else if (info.tasks > 0 && column.counts_toward_progress && column.progress_percent >= 100) state = 'done';
      else if (info.tasks > 0 && column.counts_toward_progress && !column.marks_complete) state = 'current';
      return {
        ...column,
        task_count: info.tasks,
        state,
      };
    });

  const current = stages.find((stage) => stage.state === 'current' && stage.key === focusKey) || stages.find((stage) => stage.state === 'current');
  let currentStage = current?.name || null;
  if (!currentStage && percent >= 100) {
    currentStage = stages.find((stage) => stage.marks_complete)?.name || null;
  }

  let nextStage = null;
  if (percent < 100 && current) {
    const index = stages.findIndex((stage) => stage.key === current.key);
    nextStage = stages.slice(index + 1).find((stage) => stage.counts_toward_progress)?.name || null;
  }

  return {
    percent,
    current_stage: currentStage,
    next_stage: nextStage,
    stages,
  };
}
